/**
 * Change history action hooks for document state management
 *
 * These hooks provide a Zustand-style API for change history actions,
 * calling the async functions directly with FluxServices.
 */
import {useCallback} from "react";

import type {FluxServices} from "injection/container";
import {useFluxServices} from "injection/hooks";
import {actionCreators} from "modules/document/state/actions/changeHistoryHelpers";

/**
 * Hook to get the restoreDocumentVersion action
 *
 * Calls the restoreDocumentVersion async function directly with FluxServices.
 * This action restores the document to the currently selected version in the change history panel.
 *
 * @returns A function to restore the document to the selected version
 */
export function useRestoreDocumentVersion(): () => void {
    const services = useFluxServices();

    return useCallback(() => {
        // Call the async function directly with services
        actionCreators.restoreDocumentVersion(services);
    }, [services]);
}

/**
 * Factory function to create change history actions for non-React contexts
 *
 * Use this in helper classes, services, or other non-React modules that need
 * to dispatch change history actions.
 *
 * @param services - The FluxServices container
 * @returns An object with change history action methods
 */
export function createChangeHistoryActions(services: FluxServices) {
    return {
        restoreDocumentVersion: () => {
            // Call the async function directly with services
            actionCreators.restoreDocumentVersion(services);
        },
    };
}
